import clsx from "clsx";
import {
  ChevronRight,
  CircleAlert,
  ClipboardPaste,
  Copy,
  CopyPlus,
  GripVertical,
  Link2,
  ListOrdered,
  MoreHorizontal,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";

import type { App6Palette } from "../../api/types";
import { motion } from "../../components/presence";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu";
import { Badge, Button, IconButton, inputSmClass } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { k, t, tServer } from "../../i18n/i18n";
import { echelonShort, echelonUnitName } from "./app6";
import type { UnitInfo } from "./editorModel";
import { eniRoleLabel } from "./eni";
import type { DropPosition } from "./treeDrag";
import { UnitSymbol } from "./UnitSymbol";

/** Ligne d'unité de l'arbre (charte : `min-h-9 py-1.5`, filet en bas, retrait de 20 px par niveau) : poignée de
 *  glissement, chevron, symbole sans numéros, « T ⁄ M » en chasse fixe (M en gris avec un lien quand il suit la
 *  formation), échelon court, nom (modifiable dans la ligne), rôle ennemi, place dans la manœuvre, contrôles qui la
 *  désignent, actions. */

export type LineAction = "add2" | "add3" | "add4" | "copy" | "paste" | "renumber" | "remove";

const EXCLUDED: Record<string, string> = {
  soutien: k("Non placée (soutien)"),
  surnombre: k("Non placée (surnombre)"),
  renfort: k("Non placée (renfort)"),
  rang: k("Non placée (échelon)"),
};

export function UnitLine({
  info,
  palette,
  enemy,
  editable,
  collapsed,
  selected,
  renaming,
  drop,
  dragging,
  canPaste,
  onSelect,
  onToggle,
  onRename,
  onStartRename,
  onRenameEnd,
  onAdd,
  onDuplicate,
  onAction,
  onCatalogueDuplicate,
  onHandlePointerDown,
}: {
  info: UnitInfo;
  palette: App6Palette;
  enemy: boolean;
  editable: boolean;
  collapsed: boolean;
  selected: boolean;
  renaming: boolean;
  /** Ligne d'insertion du glisser-déposer qui la vise (refusée : en rouge). */
  drop: { position: DropPosition; valid: boolean } | null;
  dragging: boolean;
  canPaste: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onRename: (name: string) => void;
  onStartRename: () => void;
  onRenameEnd: (cancel: boolean) => void;
  onAdd: () => void;
  onDuplicate: () => void;
  onAction: (action: LineAction) => void;
  /** Catalogue : copie modifiable de cette unité seule (GTIA, sous-arbre ennemi). */
  onCatalogueDuplicate?: () => void;
  onHandlePointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  const { node, path, echelon, tf, number, higher, issues } = info;
  const depth = path.length;
  const hasChildren = node.children.length > 0;
  const [menu, setMenu] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (renaming) input.current?.select();
  }, [renaming]);
  const errors = issues.filter((i) => i.level === "error");
  const warnings = issues.filter((i) => i.level === "warning");
  const placement = info.server?.placement ?? null;
  const childLabel = info.childEchelon ? echelonUnitName(palette, info.childEchelon, null) : "";
  // Nom à l'écran : désignation OTAN en anglais (« Compagnie 1 » → « Coy 1 ») ; le champ de saisie garde le nom tel quel.
  const display = unitDesignation(node.name.trim()) || unitDesignation(info.code) || t("Sans nom");
  const actionsClass = clsx(
    "flex shrink-0 items-center gap-0.5 transition-opacity duration-(--gx-enter)",
    selected || menu ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100",
  );
  return (
    <div
      role="treeitem"
      aria-level={depth + 1}
      aria-selected={selected}
      aria-expanded={hasChildren ? !collapsed : undefined}
      aria-label={display}
      data-unit-row={info.key}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      className={clsx(
        "group relative flex min-h-9 items-center gap-1.5 border-b border-line py-1.5 pr-2 outline-none transition-colors",
        selected ? "bg-selected" : "hover:bg-hover",
        drop?.position === "inside" && (drop.valid ? "bg-accent-soft" : "bg-danger-soft"),
        dragging && "opacity-40",
        "focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-inset",
      )}
      style={{ paddingLeft: 4 + depth * 20 }}
    >
      {drop && drop.position !== "inside" && (
        <span
          aria-hidden
          className={clsx(
            "pointer-events-none absolute right-2 h-0.5 rounded-full",
            drop.position === "before" ? "-top-px" : "-bottom-px",
            drop.valid ? "bg-accent" : "bg-danger",
            motion("fade"),
          )}
          style={{ left: 4 + depth * 20 }}
        />
      )}
      {editable && depth > 0 ? (
        <span
          role="presentation"
          data-drag-handle=""
          title={t("Glisser pour déplacer")}
          onPointerDown={onHandlePointerDown}
          onClick={(e) => e.stopPropagation()}
          className="flex size-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-ink-muted opacity-0 transition-opacity duration-(--gx-enter) group-hover:opacity-100 pointer-coarse:opacity-100"
        >
          <GripVertical size={14} />
        </span>
      ) : (
        <span className="size-5 shrink-0" aria-hidden />
      )}
      <IconButton
        size="xs"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        disabled={!hasChildren}
        aria-label={collapsed ? t("Déplier") : t("Replier")}
        className="shrink-0 disabled:invisible"
      >
        <ChevronRight size={14} className={clsx("transition-transform duration-(--gx-enter)", !collapsed && "rotate-90")} />
      </IconButton>
      <UnitSymbol sidc={info.sidc} size={13} box={36} />
      <span
        className="w-[68px] shrink-0 truncate font-mono text-meta tabular-nums @max-[440px]:w-14"
        title={t("En bas à gauche : {t} · en bas à droite : {m}", { t: number ?? "—", m: higher.value ?? "—" })}
      >
        <span className={number ? "text-ink" : "text-ink-muted"}>{number ?? "–"}</span>
        <span className="text-ink-muted"> ⁄ </span>
        <span className={clsx("inline-flex items-center gap-0.5", higher.auto ? "text-ink-muted" : "text-ink")}>
          {higher.value ?? "–"}
          {higher.auto && higher.value && <Link2 size={10} aria-hidden />}
        </span>
      </span>
      {/* Échelon court (« Cie », « S/GTIA ») : visible dès que l'arbre a 440 px (iPad compris), masqué au téléphone. */}
      <span className="w-12 shrink-0 truncate text-meta text-ink-soft @max-[440px]:hidden" title={echelonShort(echelon, tf)}>
        {echelonShort(echelon, tf)}
      </span>
      {renaming ? (
        <input
          ref={input}
          value={node.name}
          maxLength={80}
          aria-label={t("Nom de l'unité")}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onRename(e.target.value)}
          onBlur={() => onRenameEnd(false)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") onRenameEnd(false);
            if (e.key === "Escape") {
              e.preventDefault();
              onRenameEnd(true);
            }
          }}
          className={clsx(inputSmClass, "min-w-0 flex-1 font-medium")}
        />
      ) : (
        <span
          className={clsx("min-w-24 flex-auto truncate text-ui @max-[440px]:min-w-16", node.name.trim() ? "font-medium text-ink" : "text-ink-muted")}
          onDoubleClick={(e) => {
            if (!editable) return;
            e.stopPropagation();
            onStartRename();
          }}
          title={enemy && info.code ? `${display} — ${t("Code {code} : cité par l'ordre, calculé à chaque modification", { code: unitDesignation(info.code) })}` : display}
        >
          {display}
        </span>
      )}
      {/* Rôle ennemi : se réduit avant le nom (« Compagnie 1 » reste lisible à côté de « Premier échelon »). */}
      {enemy && info.role && (
        <span className="flex min-w-0 shrink-[3] overflow-hidden rounded-md @max-[400px]:hidden">
          <Badge size="sm" tone="hostile" title={eniRoleLabel(info.role)}>
            {eniRoleLabel(info.role)}
          </Badge>
        </span>
      )}
      {placement && (placement.slot !== null || placement.excluded) && (
        <span className="max-w-[40%] min-w-0 shrink-[4] truncate @max-[440px]:hidden">
          {placement.slot !== null ? (
            <Badge size="sm" tone="accent" title={t("Emplacement {n} sur {total} de la manœuvre", { n: placement.slot + 1, total: placement.slots })}>
              {t("Empl. {n}/{total}", { n: placement.slot + 1, total: placement.slots })}
            </Badge>
          ) : (
            <span className="text-meta text-ink-muted" title={t(EXCLUDED[placement.excluded ?? ""] ?? k("Non placée"))}>
              {t(EXCLUDED[placement.excluded ?? ""] ?? k("Non placée"))}
            </span>
          )}
        </span>
      )}
      {(errors.length > 0 || warnings.length > 0) && (
        <span
          className={clsx("flex shrink-0", errors.length ? "text-danger" : "text-warn")}
          title={[...errors, ...warnings].map((i) => tServer(i.message)).join("\n")}
          role="img"
          aria-label={errors.length ? t("Erreur à corriger") : t("Point à vérifier")}
        >
          {errors.length ? <CircleAlert size={14} /> : <TriangleAlert size={14} />}
        </span>
      )}
      {editable ? (
        <span className={actionsClass} onClick={(e) => e.stopPropagation()}>
          {/* Ligne étroite (téléphone) : ajout et duplication passent par « ⋯ ». */}
          {info.childEchelon && (
            <IconButton
              size="xs"
              className="@max-[440px]:hidden"
              title={t("Ajouter un subordonné ({type})", { type: childLabel.toLowerCase() })}
              onClick={onAdd}
            >
              <Plus size={14} />
            </IconButton>
          )}
          {depth > 0 && (
            <IconButton size="xs" className="@max-[440px]:hidden" title={t("Dupliquer cette unité et ses subordonnés")} onClick={onDuplicate}>
              <CopyPlus size={14} />
            </IconButton>
          )}
          <Menu
            open={menu}
            onClose={() => setMenu(false)}
            portal
            align="end"
            width={260}
            ariaLabel={t("Actions sur l'unité")}
            trigger={
              <IconButton size="xs" title={t("Plus d'actions")} aria-label={t("Plus d'actions")} onClick={() => setMenu((o) => !o)}>
                <MoreHorizontal size={14} />
              </IconButton>
            }
          >
            {info.childEchelon && (
              <>
                <MenuItem icon={Plus} label={t("Ajouter un subordonné ({type})", { type: childLabel.toLowerCase() })} onSelect={onAdd} />
                {([2, 3, 4] as const).map((n) => (
                  <MenuItem key={n} icon={Plus} label={t("Ajouter {n} subordonnés", { n })} onSelect={() => onAction(`add${n}`)} />
                ))}
                <MenuSeparator />
              </>
            )}
            {depth > 0 && <MenuItem icon={CopyPlus} label={t("Dupliquer cette unité et ses subordonnés")} onSelect={onDuplicate} />}
            <MenuItem icon={Copy} label={t("Copier la branche")} onSelect={() => onAction("copy")} />
            {info.childEchelon && (
              <MenuItem
                icon={ClipboardPaste}
                label={t("Coller comme subordonné")}
                disabled={!canPaste}
                hint={canPaste ? undefined : t("Copiez d'abord une branche d'un ordre de bataille du même camp.")}
                onSelect={() => onAction("paste")}
              />
            )}
            {hasChildren && <MenuItem icon={ListOrdered} label={t("Numéroter les subordonnés 1, 2, 3…")} onSelect={() => onAction("renumber")} />}
            {depth > 0 && (
              <>
                <MenuSeparator />
                <MenuItem icon={Trash2} danger label={t("Retirer cette unité et ses subordonnés")} onSelect={() => onAction("remove")} />
              </>
            )}
          </Menu>
        </span>
      ) : (
        onCatalogueDuplicate && (
          <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCatalogueDuplicate}
              title={enemy ? t("Copie modifiable de cette unité seule, avec ses subordonnés") : t("Copie modifiable de ce GTIA seul")}
            >
              <Copy size={14} /> {t("Dupliquer")}
            </Button>
          </span>
        )
      )}
    </div>
  );
}
